import { forwardRef, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import type { LinearProgressProps } from '@mui/material/LinearProgress';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled as muiStyled } from '@mui/material/styles';
import type { RootNode } from '@trinity/chat/types/rich-text';
import { Lightbulb } from '@trinity/chat-components/components/Chat.Widget/Lightbulb.js';
import { BasicRichText } from '../BasicRichText/BasicRichText';

// ─── ProgressBar (inlined primitive) ─────────────────────────────────────────

type BorderLinearProgressProps = LinearProgressProps & {
  backgroundColor?: string;
  backgroundColorDark?: string;
  foregroundColor?: string;
  foregroundColorDark?: string;
  minValue?: number;
  maxValue?: number;
};

const BorderLinearProgress = muiStyled(
  (props: BorderLinearProgressProps) => <LinearProgress {...props} />)(({ theme, backgroundColor, backgroundColorDark, foregroundColor, foregroundColorDark }) => ({
  height: 20,
  borderRadius: 10,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: backgroundColor || '#eeeeee',
    ...theme.applyStyles?.('dark', {
      backgroundColor: backgroundColorDark || '#424242',
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 10,
    backgroundColor: foregroundColor || '#1a90ff',
    ...theme.applyStyles?.('dark', {
      backgroundColor: foregroundColorDark || '#308fe8',
    }),
  },
}));

type ProgressBarProps = {
  progress: number;
  backgroundColor?: string;
  backgroundColorDark?: string;
  foregroundColor?: string;
  foregroundColorDark?: string;
  minValue?: number;
  maxValue?: number;
};

const ProgressBar = memo(function ProgressBar(props: ProgressBarProps) {
  const minValue = props.minValue ?? 0;
  const maxValue = props.maxValue ?? 100;
  const normalizedValue = ((props.progress - minValue) / (maxValue - minValue)) * 100;

  return (
    <Box sx={{ width: '100%', height: 20 }}>
      <BorderLinearProgress
        variant="determinate"
        value={normalizedValue}
        backgroundColor={props.backgroundColor}
        backgroundColorDark={props.backgroundColorDark}
        foregroundColor={props.foregroundColor}
        foregroundColorDark={props.foregroundColorDark}
      />
    </Box>
  );
});

type Props = {
  title: string;
  backgroundColor: string;
  values: string[];
  minValues: string[];
  maxValues: string[];
  labels: string[];
  colors?: string[];
  units?: string[];
  insights?: string;
};

export const ProgressBarWidget = forwardRef<HTMLDivElement, Props>(({
  title,
  backgroundColor,
  values,
  minValues,
  maxValues,
  labels,
  colors,
  units,
  insights,
}, ref) => {
  const getGridLayout = (count: number): string => {
    switch (count) {
      case 1:
        return '1fr';
      case 2:
      case 3:
        return `repeat(${count}, 1fr)`;
      case 4:
        return 'repeat(2, 1fr)';
      case 5:
        return 'repeat(3, 1fr)';
      case 6:
        return 'repeat(3, 1fr)';
      default:
        return '1fr';
    }
  };

  const getGridRows = (count: number): string => {
    switch (count) {
      case 1:
      case 2:
      case 3:
        return '1fr';
      case 4:
      case 6:
        return 'repeat(2, 1fr)';
      case 5:
        return 'auto auto';
      default:
        return '1fr';
    }
  };

  const validValues = values.slice(0, 6); // Ensure max 6 values
  const validLabels = labels.slice(0, 6); // Ensure max 6 labels
  const validMinValues = minValues.slice(0, 6); // Ensure max 6 min values
  const validMaxValues = maxValues.slice(0, 6); // Ensure max 6 max values
  const validColors = colors ? colors.slice(0, 6) : undefined; // Ensure max 6 colors
  const validUnits = units ? units.slice(0, 6) : Array(6).fill('%'); // Ensure max 6 units, default to %

  // Memoized insights parsing - only recalculates when insights prop changes
  const renderedInsights = useMemo(() => {
    if (!insights) return null;

    try {
      const parsedInsights = JSON.parse(insights) as RootNode;
      return <BasicRichText value={parsedInsights} />;
    } catch {
      return null;
    }
  }, [insights]);

  return (
    <ProgressBarContainer ref={ref} backgroundColor={backgroundColor}>
      <ProgressBarHeader>
        <ProgressBarTitle>{title}</ProgressBarTitle>
      </ProgressBarHeader>

      <ProgressBarValuesGrid
        count={validValues.length}
        gridColumns={getGridLayout(validValues.length)}
        gridRows={getGridRows(validValues.length)}>
        {validValues.map((value, index) => {
          const label = validLabels[index] || `KPI ${index + 1}`;
          const numericValue = Number(value);
          const minRaw = Number(validMinValues[index] ?? '0');
          const maxRaw = Number(validMaxValues[index] ?? '100');
          const minValue = isNaN(minRaw) ? 0 : minRaw;
          const maxValue = isNaN(maxRaw) ? 100 : maxRaw;
          const range = maxValue - minValue;
          const normalizedValue = range === 0 ? 0 : ((numericValue - minValue) / range) * 100;
          const percentage = Math.round(normalizedValue);

          // Handle special layout for 5 values - first 3 in first row, last 2 in second row
          const gridColumn = validValues.length === 5 && index >= 3
            ? `${index - 2} / ${index - 1}`
            : undefined;

          return (
            <ProgressBarValueItem key={index} gridColumn={gridColumn}>
              <ProgressBarValueLabel>{label}</ProgressBarValueLabel>
              <ProgressBar progress={numericValue} minValue={minValue} maxValue={maxValue} foregroundColor={validColors ? validColors[index] : undefined} />
              <ProgressBarValueNumber>{percentage}{validUnits[index]}</ProgressBarValueNumber>
            </ProgressBarValueItem>
          );
        })}
      </ProgressBarValuesGrid>

      {insights && <ProgressBarInsights><Lightbulb fill={'#4c4c4c'} size={12} />{renderedInsights}</ProgressBarInsights>}
    </ProgressBarContainer>
  );
});

ProgressBarWidget.displayName = 'ProgressBarWidget';

const ProgressBarContainer = styled.div<{ backgroundColor: string }>(({ backgroundColor, theme }) => ({
  backgroundColor: backgroundColor || '#ffffff',
  containerType: 'inline-size',
  color: theme.palette.text.primary,
  borderRadius: '1.125em',
  padding: '1.25em',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '1em',
  boxSizing: 'border-box',
}));

const ProgressBarHeader = styled.div({
  textAlign: 'left',
});

const ProgressBarTitle = styled.h3(({ theme }) => ({
  margin: 0,
  fontSize: '1em',
  fontWeight: 700,
  color: theme.palette.text.primary,
  // Add your container query here
  '@container (max-width: 200px)': {
    fontSize: '0.80em',
  },
}));

const ProgressBarValuesGrid = styled.div<{
  count: number;
  gridColumns: string;
  gridRows: string;
}>(({ count, gridColumns, gridRows }) => ({
  display: 'grid',
  gridTemplateColumns: gridColumns,
  gridTemplateRows: gridRows,
  gap: '1em',
  flex: 1,
  alignItems: 'center',
  overflowX: 'auto',
  ...(count === 1 && {
    justifyContent: 'center',
    alignContent: 'center',
  }),
  '@container (max-width: 200px)': {
    gap: '0.5em',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'auto',
  },
  '@container (max-width: 140px)': {
    gap: '0.5em',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto',
  },
}));

const ProgressBarValueItem = styled.div<{ gridColumn?: string }>(({ gridColumn }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '0.75em',
  width: '100%',
  ...(gridColumn && {
    gridColumn,
  }),
}));

const ProgressBarValueNumber = styled.div(({ theme }) => ({
  fontSize: '0.875em',
  fontWeight: 600,
  color: theme.palette.text.primary,
  minWidth: '3em',
  textAlign: 'right',
  flexShrink: 0,
  '@container (max-width: 200px)': {
    fontSize: '0.75em',
    minWidth: '2.5em',
  },
}));

const ProgressBarValueLabel = styled.div(({ theme }) => ({
  fontSize: '0.75em',
  color: theme.palette.text.primary,
  fontWeight: 500,
  textAlign: 'left',
  flexShrink: 0,
  minWidth: '8em',
  maxWidth: '12em',
  wordBreak: 'break-word',
  '@container (max-width: 200px)': {
    fontSize: '0.6em',
    minWidth: '6em',
  },
}));

const ProgressBarInsights = styled.div(() => ({
  display: 'grid',
  gap: '0.5em',
  gridTemplateColumns: 'auto 1fr',
  '@container (max-width: 200px)': {
    fontSize: '0.80em',
    'p': {
      fontSize: '0.80em',
    },
    'strong': { fontSize: '0.80em' },
    'em': { fontSize: '0.80em' },
    'strong em': { fontSize: '0.80em' },
    '> svg': {
      width: '10px',

    },
  },
}));